import Layout from "@/components/Layout";
import PageHero from "@/components/PageHero";
import { Section, SectionLabel, SectionTitle, FadeIn } from "@/components/Section";
import { Globe, Ship, Clock, Radio, Compass, BarChart3, ArrowRight, Star, ClipboardCheck, CheckCircle, Anchor, ChevronDown } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { MOCK_VESSELS, MOCK_VOYAGES } from "@/data/portalMockData";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import operationsBg from "@/assets/operations-bg.jpg";

const operationsCards = [
  { icon: Globe, title: "Global Reach", desc: "Operational footprint designed for offshore operations in regions such as West Africa, the Caribbean, South America, and the Guyana-Suriname basin with 24/7 shore-based support." },
  { icon: Ship, title: "Fleet Management", desc: "Comprehensive vessel management covering crewing, maintenance planning, class surveys, dry-docking, and flag-state compliance." },
  { icon: Clock, title: "24/7 Operations Centre", desc: "Round-the-clock monitoring and coordination ensuring real-time decision-making, incident response, and voyage optimisation." },
  { icon: Radio, title: "Communications", desc: "Redundant VSAT, Fleet Broadband, and VHF/HF systems ensuring continuous connectivity between vessels, shore, and clients." },
  { icon: Compass, title: "Voyage Planning", desc: "Detailed passage plans, weather routing, and port-state requirements managed by experienced master mariners and DPAs." },
  { icon: BarChart3, title: "Performance Tracking", desc: "KPI dashboards covering fuel efficiency, uptime, HSEQ metrics, and client satisfaction with monthly reporting and trend analysis." },
];

const keyFacts = [
  { value: "24/7", label: "Operations Centre" },
  { value: "99.2%", label: "Fleet Uptime" },
  { value: "<2hr", label: "Avg. Response Time" },
  { value: "6+", label: "Operating Regions" },
];

const FPSO_RIGS = [
  "Liza Unity FPSO",
  "Liza Destiny FPSO",
  "Prosperity FPSO",
  "Yellowtail One FPSO",
  "Uaru FPSO",
  "Stena DrillMAX",
  "Noble Bob Douglas",
  "Noble Tom Madden",
  "Noble Sam Croft",
  "Maersk Deliverer",
  "Maersk Developer",
];

/* ── Premium Satisfaction Survey Modal ── */
const SatisfactionSurveyModal = () => {
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [npsScore, setNpsScore] = useState<number | null>(null);
  const [comments, setComments] = useState("");
  const [voyageRef, setVoyageRef] = useState("");
  const [vessel, setVessel] = useState("");
  const [fpsoRig, setFpsoRig] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [open, setOpen] = useState(false);

  const handleStarClick = (field: string, rating: number) =>
    setRatings((prev) => ({ ...prev, [field]: rating }));

  const StarRow = ({ field, label }: { field: string; label: string }) => (
    <div className="flex items-center justify-between py-3 border-b border-primary-foreground/5 last:border-0">
      <span className="text-sm text-primary-foreground/80 font-medium">{label}</span>
      <div className="flex gap-1.5">
        {[1, 2, 3, 4, 5].map((s) => (
          <button
            key={s}
            onClick={() => handleStarClick(field, s)}
            className="p-0.5 transition-all hover:scale-125 active:scale-95"
          >
            <Star
              className={`w-7 h-7 transition-colors ${
                (ratings[field] || 0) >= s
                  ? "text-amber-400 fill-amber-400 drop-shadow-[0_0_6px_rgba(251,191,36,0.4)]"
                  : "text-primary-foreground/15 hover:text-primary-foreground/30"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );

  const handleSubmit = async () => {
    if (!vessel) {
      toast({ title: "Missing Info", description: "Please select a vessel.", variant: "destructive" });
      return;
    }
    if (!fpsoRig) {
      toast({ title: "Missing Info", description: "Please select the FPSO / Rig destination.", variant: "destructive" });
      return;
    }
    if (!ratings.overall) {
      toast({ title: "Missing Info", description: "Please provide at least an overall rating.", variant: "destructive" });
      return;
    }
    setSubmitting(true);

    const matchedVessel = MOCK_VESSELS.find((v) => v.name === vessel);
    const clientName = matchedVessel?.client || null;

    const captainMap: Record<string, string> = {
      "Velocity Pioneer": "Capt. R. Maharaj",
      "Velocity Endeavour": "Capt. J. Singh",
      "Velocity Benguela": "Capt. M. Da Silva",
    };
    const crewMap: Record<string, string[]> = {
      "Velocity Pioneer": ["C/O D. Persaud", "Eng. S. Ramkissoon", "A/B K. Williams"],
      "Velocity Endeavour": ["C/O T. Bacchus", "Eng. J. Chen", "A/B L. Carter"],
      "Velocity Benguela": ["C/O A. Mendes", "Eng. R. Almeida", "A/B C. Dube"],
    };

    const refToUse = voyageRef.trim().toUpperCase() || `VOY-${Date.now().toString().slice(-6)}`;

    const { error } = await supabase.from("satisfaction_surveys" as any).insert({
      voyage_ref: refToUse,
      vessel_name: vessel,
      client_name: clientName,
      rating_overall: ratings.overall || null,
      rating_crew: ratings.crew || null,
      rating_vessel: ratings.vessel || null,
      rating_punctuality: ratings.punctuality || null,
      rating_communication: ratings.communication || null,
      rating_safety: ratings.safety || null,
      nps_score: npsScore,
      comments: comments ? `[FPSO/Rig: ${fpsoRig}] ${comments}` : `[FPSO/Rig: ${fpsoRig}]`,
      captain_name: captainMap[vessel] || null,
      crew_on_shift: JSON.stringify(crewMap[vessel] || []),
    });

    setSubmitting(false);
    if (error) {
      toast({ title: "Submission Failed", description: "Please try again later.", variant: "destructive" });
      return;
    }
    setSubmitted(true);
  };

  const resetSurvey = () => {
    setRatings({});
    setNpsScore(null);
    setComments("");
    setVoyageRef("");
    setVessel("");
    setFpsoRig("");
    setSubmitted(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetSurvey(); }}>
      <DialogTrigger asChild>
        <button className="group w-full">
          <div className="relative bg-gradient-to-r from-navy-deep via-[hsl(210,60%,12%)] to-navy-deep rounded-2xl border border-teal/20 overflow-hidden hover:border-teal/40 transition-all duration-500 hover:shadow-2xl hover:shadow-teal/10">
            {/* Animated glow border */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-teal/0 via-teal/5 to-lime/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-teal to-transparent opacity-60 group-hover:opacity-100 transition-opacity" />
            
            <div className="relative flex items-center justify-between p-6 md:p-8">
              <div className="flex items-center gap-5">
                <div className="relative">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal/20 to-lime/10 flex items-center justify-center group-hover:from-teal/30 group-hover:to-lime/20 transition-colors">
                    <ClipboardCheck className="w-8 h-8 text-teal" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center">
                    <Star className="w-3 h-3 text-navy-deep fill-navy-deep" />
                  </div>
                </div>
                <div className="text-left">
                  <div className="text-[10px] font-bold tracking-[3px] uppercase text-teal/70 mb-1">Passenger Feedback</div>
                  <h3 className="font-display text-xl md:text-2xl text-primary-foreground tracking-tight">
                    Customer Satisfaction <span className="text-teal">Survey</span>
                  </h3>
                  <p className="text-primary-foreground/40 text-sm mt-1 max-w-md">
                    Rate your voyage experience — no login required. Your feedback drives our continuous improvement.
                  </p>
                </div>
              </div>
              <div className="hidden md:flex items-center gap-3">
                <span className="text-[11px] font-bold tracking-[1.5px] uppercase text-teal/80">Take Survey</span>
                <div className="w-10 h-10 rounded-full border border-teal/30 flex items-center justify-center group-hover:bg-teal/10 group-hover:border-teal/50 transition-all">
                  <ArrowRight className="w-5 h-5 text-teal group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            </div>
          </div>
        </button>
      </DialogTrigger>

      <DialogContent className="max-w-lg p-0 border-0 bg-transparent shadow-none [&>button]:text-primary-foreground [&>button]:hover:text-teal">
        <div className="gradient-navy rounded-2xl border border-primary-foreground/10 shadow-2xl shadow-black/50 overflow-hidden max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="relative p-8 pb-6 text-center border-b border-primary-foreground/5">
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-teal to-transparent" />
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal/20 to-lime/10 flex items-center justify-center mx-auto mb-4">
              <ClipboardCheck className="w-8 h-8 text-teal" />
            </div>
            <h2 className="font-display text-2xl text-primary-foreground tracking-tight">
              Customer Satisfaction <span className="text-teal">Survey</span>
            </h2>
            <p className="text-primary-foreground/40 text-sm mt-2">No login required — your feedback is invaluable</p>
          </div>

          {submitted ? (
            <div className="p-10 text-center">
              <div className="w-20 h-20 rounded-full bg-teal/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-teal" />
              </div>
              <h3 className="font-display text-xl text-primary-foreground mb-2">Thank You!</h3>
              <p className="text-primary-foreground/50 text-sm mb-6">Your feedback has been submitted successfully and will be reviewed by our operations team.</p>
              <button onClick={() => { resetSurvey(); setOpen(false); }} className="px-6 py-3 bg-teal/10 text-teal text-sm font-bold rounded-xl hover:bg-teal/20 transition-colors">
                Close
              </button>
            </div>
          ) : (
            <div className="p-8 space-y-6">
              {/* Vessel & FPSO Selection */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/50 mb-2">
                    <Ship className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Vessel
                  </label>
                  <div className="relative">
                    <select
                      value={vessel}
                      onChange={(e) => setVessel(e.target.value)}
                      className="w-full px-4 py-3 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground text-sm appearance-none cursor-pointer focus:outline-none focus:border-teal/40 focus:ring-1 focus:ring-teal/20 transition-all"
                    >
                      <option value="" className="bg-navy-deep">Select vessel…</option>
                      {MOCK_VESSELS.map((v) => (
                        <option key={v.id} value={v.name} className="bg-navy-deep">{v.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-foreground/30 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/50 mb-2">
                    <Anchor className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />FPSO / Rig
                  </label>
                  <div className="relative">
                    <select
                      value={fpsoRig}
                      onChange={(e) => setFpsoRig(e.target.value)}
                      className="w-full px-4 py-3 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground text-sm appearance-none cursor-pointer focus:outline-none focus:border-teal/40 focus:ring-1 focus:ring-teal/20 transition-all"
                    >
                      <option value="" className="bg-navy-deep">Select destination…</option>
                      {FPSO_RIGS.map((r) => (
                        <option key={r} value={r} className="bg-navy-deep">{r}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-foreground/30 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Voyage Ref (optional) */}
              <div>
                <label className="block text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/50 mb-2">Voyage Reference <span className="text-primary-foreground/25">(optional)</span></label>
                <input
                  type="text"
                  value={voyageRef}
                  onChange={(e) => setVoyageRef(e.target.value)}
                  className="w-full px-4 py-3 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 focus:ring-1 focus:ring-teal/20 transition-all"
                  placeholder="e.g. VOY-0413"
                />
              </div>

              {/* Star Ratings */}
              <div className="bg-primary-foreground/[0.02] rounded-xl p-5 border border-primary-foreground/5">
                <StarRow field="overall" label="Overall Voyage Rating" />
                <StarRow field="crew" label="Crew Professionalism" />
                <StarRow field="vessel" label="Vessel Comfort & Cleanliness" />
                <StarRow field="punctuality" label="Punctuality / On-Time" />
                <StarRow field="communication" label="Communication from Ops" />
                <StarRow field="safety" label="Safety Impression" />
              </div>

              {/* NPS */}
              <div>
                <label className="block text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/50 mb-3">
                  How likely are you to recommend Velocity Offshore? (0–10)
                </label>
                <div className="flex gap-1.5 flex-wrap">
                  {Array.from({ length: 11 }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => setNpsScore(i)}
                      className={`w-10 h-10 rounded-xl border text-xs font-bold transition-all duration-200 ${
                        npsScore === i
                          ? "bg-teal text-navy-deep border-teal shadow-lg shadow-teal/20"
                          : "border-primary-foreground/15 text-primary-foreground/60 hover:border-teal/40 hover:text-primary-foreground hover:bg-primary-foreground/5"
                      }`}
                    >
                      {i}
                    </button>
                  ))}
                </div>
              </div>

              {/* Comments */}
              <div>
                <label className="block text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/50 mb-2">Comments</label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  className="w-full px-4 py-3 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 focus:ring-1 focus:ring-teal/20 transition-all min-h-[80px] resize-none"
                  placeholder="Any feedback or comments…"
                />
              </div>

              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-xl hover:shadow-teal/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Submitting…" : "Submit Survey"}
                {!submitting && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Operations = () => (
  <Layout>
    <PageHero
      tag="Operations"
      title={<>Precision execution, <span className="accent-text">worldwide.</span></>}
      subtitle="Our Operations team delivers safe, reliable vessel management and offshore personnel transportation across some of the world's most demanding Marine Operations environments."
      backgroundImage={operationsBg}
    />

    <Section>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
        {keyFacts.map((fact, i) => (
          <FadeIn key={fact.label} delay={i * 0.08}>
            <div className="text-center">
              <div className="font-display text-4xl md:text-5xl text-teal tracking-tight mb-2">{fact.value}</div>
              <div className="text-text-light font-body text-sm">{fact.label}</div>
            </div>
          </FadeIn>
        ))}
      </div>

      <SectionLabel>Capabilities</SectionLabel>
      <SectionTitle>Operational <span className="accent-text">excellence</span></SectionTitle>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
        {operationsCards.map((card, i) => (
          <FadeIn key={card.title} delay={i * 0.08}>
            <div className="bg-card rounded-2xl p-8 border border-border hover-lift group relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-teal to-lime opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal/10 to-lime/10 flex items-center justify-center mb-5 group-hover:from-teal/20 group-hover:to-lime/20 transition-colors">
                <card.icon className="w-6 h-6 text-teal" />
              </div>
              <h3 className="font-display text-lg text-navy tracking-tight mb-3">{card.title}</h3>
              <p className="text-text-light font-body text-sm leading-relaxed">{card.desc}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </Section>

    {/* Customer Satisfaction Survey CTA */}
    <Section offWhite>
      <FadeIn>
        <SatisfactionSurveyModal />
      </FadeIn>
    </Section>


    <Section dark>
      <div className="text-center">
        <SectionLabel>Our Commitment</SectionLabel>
        <SectionTitle light>Mission-critical <span className="accent-text">reliability</span></SectionTitle>
        <p className="text-primary-foreground/50 font-body text-lg max-w-2xl mx-auto leading-relaxed mt-4">
          Safety, reliability, and operational uptime underpin every mission. Every operation is backed by rigorous planning, experienced personnel, and operationally proven systems designed to deliver on time, every time — no matter the conditions.
        </p>
      </div>
    </Section>
  </Layout>
);

export default Operations;
